import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { FileText, Calendar, Download, FileSpreadsheet } from 'lucide-react';

export default function AttendanceSummary() {
  const [summary, setSummary] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedLocation, setSelectedLocation] = useState('all');

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  function getMonthOptions() {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [selectedMonth, selectedLocation]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);

      const response = await api.get(`/attendance/summary?${params.toString()}`);
      setSummary(response.data.employees || []);
    } catch (error) {
      toast.error('Failed to fetch summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);

      const response = await api.get(`/attendance/summary/export/pdf?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Summary_-_${selectedMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      if (selectedLocation !== 'all') params.append('location_id', selectedLocation);

      const response = await api.get(`/attendance/summary/export/excel?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Summary_-_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exported successfully');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="summary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Attendance Summary</h1>
          <p className="text-slate-500">Monthly attendance overview by employee</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportPDF}
            className="gap-2"
            data-testid="summary-export-pdf-btn"
          >
            <Download className="w-4 h-4" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="gap-2"
            data-testid="summary-export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            Select Period
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger data-testid="month-filter">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Location</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="summary-location-filter">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Table */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-500" />
            Employee Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="summary-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Employee Name</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold text-center">Working Days</TableHead>
                  <TableHead className="font-semibold text-center">Present Days</TableHead>
                  <TableHead className="font-semibold text-center">Absent Days</TableHead>
                  <TableHead className="font-semibold text-center">Paid Leave</TableHead>
                  <TableHead className="font-semibold text-center">Unpaid Leave</TableHead>
                  <TableHead className="font-semibold text-center">Total Hours</TableHead>
                  <TableHead className="font-semibold text-center">OT Days</TableHead>
                  <TableHead className="font-semibold text-center">OT Hours</TableHead>
                  <TableHead className="font-semibold text-center">Late Days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : summary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      No data available for selected period
                    </TableCell>
                  </TableRow>
                ) : (
                  summary.map((item) => (
                    <TableRow key={item.employee_id} data-testid={`summary-row-${item.employee_id}`}>
                      <TableCell className="font-medium text-slate-800">{item.employee_name}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{item.location_name}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                          {item.working_days}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-[#138808] hover:bg-[#107307]">
                          {item.present_days}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.absent_days > 0 ? (
                          <Badge variant="destructive">{item.absent_days}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.paid_leave_days > 0 ? (
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{item.paid_leave_days}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.unpaid_leave_days > 0 ? (
                          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">{item.unpaid_leave_days}</Badge>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-slate-700 font-medium">{item.total_hours}</TableCell>
                      <TableCell className="text-center">
                        {item.overtime_days > 0 ? (
                          <span className="text-[#FF9933] font-medium">{item.overtime_days}</span>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={item.overtime_hours !== '0min' ? 'text-[#FF9933] font-medium' : 'text-slate-400'}>
                          {item.overtime_hours}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.late_days > 0 ? (
                          <Badge variant="destructive">{item.late_days}</Badge>
                        ) : (
                          <Badge className="bg-[#138808] hover:bg-[#107307]">0</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
